import { API_URL } from '../configAndUrls.js'
import { apiRequest, getAuthHeaders, createMutationIdempotencyKey } from '../http.js'

export function createContentApi() {
  const contentApi = {
getAll: async (campaignId = null, options = {}) => {
      const query = campaignId ? `?campaignId=${campaignId}` : ''
      const data = await apiRequest(`/content${query}`, options)
      return data.content || []
    },

    
    getById: async (id) => {
      const data = await apiRequest(`/content/${id}`)
      return data.content
    },

    
    getPresignedUpload: async (campaignId, filename, contentType) => {
      const params = new URLSearchParams({ campaignId, filename })
      if (contentType) params.set('contentType', contentType)
      const response = await fetch(`${API_URL}/content/presigned-upload?${params}`, {
        headers: getAuthHeaders(),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 503 && data.error === 'S3_NOT_CONFIGURED') return null
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to get upload URL')
      return data
    },

    
    createFromS3: async (payload) => {
      const data = await apiRequest('/content/from-s3', {
        method: 'POST',
        body: JSON.stringify(payload),
        idempotencyKey: createMutationIdempotencyKey('content-create-s3'),
      })
      return data.content
    },

    
    upload: async (campaignId, file, title, description, contentType) => {
      const normalizedTitle = String(title || '').trim()
      const normalizedDescription = String(description || '').trim()
      if (!normalizedTitle) throw new Error('Title is required')
      if (!normalizedDescription) throw new Error('Caption is required')

      const presigned = await contentApi.getPresignedUpload(campaignId, file.name, file.type)
      if (presigned) {
        const putRes = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        })
        if (!putRes.ok) {
          const text = await putRes.text()
          throw new Error(`S3 upload failed (${putRes.status}). Check CORS on your bucket. ${text.slice(0, 100)}`)
        }
        return contentApi.createFromS3({
          campaignId,
          s3_key: presigned.s3Key,
          public_url: presigned.publicUrl,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          title: normalizedTitle,
          description: normalizedDescription,
          content_type: contentType || null,
        })
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('campaignId', campaignId)
      formData.append('title', normalizedTitle)
      formData.append('description', normalizedDescription)
      if (contentType) formData.append('content_type', contentType)

      const response = await fetch(`${API_URL}/content/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Upload failed')
      }

      const data = await response.json()
      return data.content
    },

    
    releaseToClient: async (id) => {
      const data = await apiRequest(`/content/${id}/release-to-client`, {
        method: 'PATCH',
        idempotencyKey: createMutationIdempotencyKey('content-release'),
      })
      return data.content
    },

    updateStatus: async (id, status, reviewNote = null) => {
      const data = await apiRequest(`/content/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, ...(reviewNote != null ? { review_note: reviewNote } : {}) }),
        idempotencyKey: createMutationIdempotencyKey('content-status'),
      })
      return data.content
    },

    
    submit: async (id) => {
      const data = await apiRequest(`/content/${id}/submit`, {
        method: 'PATCH',
        idempotencyKey: createMutationIdempotencyKey('content-submit'),
      })
      return data.content
    },

    
    update: async (id, title, description, contentType, file = null, campaignIdForUpload = null) => {
      // If file is provided, use FormData; otherwise use JSON
      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        if (campaignIdForUpload) formData.append('campaignId', campaignIdForUpload)
        if (title != null) formData.append('title', title)
        if (description != null) formData.append('description', description)
        if (contentType) formData.append('content_type', contentType)

        const response = await fetch(`${API_URL}/content/${id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: formData,
        })

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Update failed')
        }

        const data = await response.json()
        return data.content
      } else {
        const data = await apiRequest(`/content/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ title, description, content_type: contentType || undefined }),
        })
        return data.content
      }
    },

    
    delete: async (id) => {
      await apiRequest(`/content/${id}`, { method: 'DELETE' })
    },

    review: {
      
      get: async (id, options = {}) => {
        return apiRequest(`/content/${id}/review`, options)
      },

      
      postMessage: async (id, scope, body) => {
        const data = await apiRequest(
          `/content/${id}/review/threads/${encodeURIComponent(scope)}/messages`,
          {
            method: 'POST',
            body: JSON.stringify({ body }),
            idempotencyKey: createMutationIdempotencyKey('content-review-message'),
          }
        )
        return data
      },

      
      publishRevision: async (id, body) => {
        const data = await apiRequest(`/content/${id}/review/publish-revision`, {
          method: 'POST',
          body: JSON.stringify({ body }),
          idempotencyKey: createMutationIdempotencyKey('content-review-publish-revision'),
        })
        return data
      },
      
      getNotifications: async ({ limit = 20, campaignId, signal } = {}) => {
        const q = new URLSearchParams()
        if (limit != null) q.set('limit', String(limit))
        if (campaignId) q.set('campaignId', String(campaignId))
        const qs = q.toString()
        return apiRequest(`/content/review/notifications${qs ? `?${qs}` : ''}`, { signal })
      },
    },
  }
  return contentApi
}
